import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  inject,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NzDrawerModule } from 'ng-zorro-antd/drawer';
import { NzTimelineModule } from 'ng-zorro-antd/timeline';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { RapiderJsonViewerComponent } from '@rapider/components/json-viewer';
import { AuthStore } from '@rapider/authentication';
import { NotificationService } from '@rapider/services';
import {
  AiLogControllerService,
  FileManagementControllerService,
} from '@rapider/sdk';
import { HttpClient } from '@angular/common/http';

import { MarkdownPreviewComponent } from '@rapider/markdown-preview';
import {
  AiToolCall,
  AIChatMessage,
  TraceNode,
  TraceTask,
} from '../ai-chat.config';
import { AgentTraceService } from '../services/agent-trace.service';

// Utility to clean up MCP responses for the UI
export function safelyParseMcpPayload(payload: any): any {
  if (!payload) return payload;

  try {
    const payloadClone = JSON.parse(JSON.stringify(payload));

    if (Array.isArray(payloadClone.content)) {
      payloadClone.content.forEach((item: any) => {
        if (item.type === 'text' && typeof item.text === 'string') {
          try {
            item.text = JSON.parse(item.text);
          } catch (e) {
            // Not JSON, leave it as a string
          }
        }
      });
    }

    return payloadClone;
  } catch (e) {
    return payload;
  }
}
@Component({
  selector: 'rapider-agent-debug-drawer',
  standalone: true,
  imports: [
    CommonModule,
    NzDrawerModule,
    NzTimelineModule,
    NzSpinModule,
    NzButtonModule,
    NzCollapseModule,
    NzTooltipModule,
    NzModalModule,
    RapiderJsonViewerComponent,
    MarkdownPreviewComponent,
  ],
  templateUrl: './agent-debug-drawer.component.html',
  styleUrls: ['./agent-debug-drawer.component.scss'],
})
export class AgentDebugDrawerComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() message: AIChatMessage | null = null;
  @Output() isOpenChange = new EventEmitter<boolean>();

  public trace: TraceNode[] = [];
  public isLoading = false;
  public errorMessage: string | null = null;
  public isExporting = false;
  public canExport = false;
  public isMaximized = false;
  public isAllExpanded = false;

  public traceSummary: {
    totalSteps: number;
    totalDurationMs: number;
    top2StepsNames: string;
    top2DurationMs: number;
    top2Percentage: number;
  } | null = null;

  public isDebugModalVisible = false;
  public debugDocumentationContent: string | null = null;
  public isLoadingDebugDoc = false;
  public userMessageInput: string | null = null;

  private readonly authStore = inject(AuthStore);
  private readonly notificationService = inject(NotificationService);
  private readonly aiLogControllerService = inject(AiLogControllerService);
  private readonly fileManagementControllerService = inject(
    FileManagementControllerService,
  );
  private readonly agentTraceService = inject(AgentTraceService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly http = inject(HttpClient);

  constructor() {
    this.canExport = true;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen'] && this.isOpen && this.message?.id) {
      this.loadTrace();
    } else if (changes['message'] && this.isOpen && this.message?.id) {
      this.loadTrace();
    }
  }

  private loadTrace(): void {
    if (!this.message?.id) return;

    this.isLoading = true;
    this.errorMessage = null;

    this.agentTraceService.getTrace(this.message.id).subscribe({
      next: (nodes) => {
        nodes.forEach((node) => {
          node.isExpanded = true;
          node.isStateExpanded = false;
          node.isEventsExpanded = false;
          node.tasks?.forEach((task) => (task.isExpanded = true));
        });

        let totalDuration = 0;
        let maxDuration = 0;
        nodes.forEach((node) => {
          const dur = node.durationMs || 0;
          totalDuration += dur;
          if (dur > maxDuration) {
            maxDuration = dur;
          }
        });

        nodes.forEach((node) => {
          const dur = node.durationMs || 0;
          if (dur === maxDuration && maxDuration > 0) {
            node.isMaxDuration = true;
          }
          if (totalDuration > 0) {
            node.durationPercentage = Math.round((dur / totalDuration) * 100);
          }
        });

        const sortedNodes = [...nodes].sort(
          (a, b) => (b.durationMs || 0) - (a.durationMs || 0),
        );
        const top2Nodes = sortedNodes.slice(0, 2);
        const top2DurationMs = top2Nodes.reduce(
          (sum, n) => sum + (n.durationMs || 0),
          0,
        );
        const top2Percentage =
          totalDuration > 0
            ? Math.round((top2DurationMs / totalDuration) * 100)
            : 0;
        const top2Names = top2Nodes
          .map((n) => this.getTaskNames(n) || n.nodeName)
          .join(', ');

        this.traceSummary = {
          totalSteps: nodes.length,
          totalDurationMs: totalDuration,
          top2StepsNames: top2Names,
          top2DurationMs: top2DurationMs,
          top2Percentage: top2Percentage,
        };

        // Extract User Message Input
        let originalInput = 'No input message found';
        if (nodes.length > 0) {
          for (const node of nodes) {
            if (node.tasks && node.tasks.length > 0) {
              for (const task of node.tasks) {
                const res = task.result;
                if (res) {
                  if (res.transcribedMessage) {
                    originalInput = res.transcribedMessage;
                    break;
                  } else if (res.originalMessage) {
                    originalInput = res.originalMessage;
                    break;
                  } else if (res.messages && res.messages.length > 0) {
                    const firstUserMsg = res.messages.find(
                      (m: any) => m.role === 'user',
                    );
                    if (firstUserMsg && firstUserMsg.content) {
                      originalInput =
                        typeof firstUserMsg.content === 'string'
                          ? firstUserMsg.content
                          : JSON.stringify(firstUserMsg.content);
                      break;
                    }
                  }
                }
              }
            }
            if (originalInput !== 'No input message found') {
              break;
            }
          }
        }

        if (
          originalInput === 'No input message found' &&
          (this.message as any)?.data?.__rapider?.transcribedMessage
        ) {
          originalInput = (this.message as any).data.__rapider
            .transcribedMessage;
        } else if ((this.message as any)?.data?.__rapider?.originalMessage) {
          originalInput = (this.message as any).data.__rapider.originalMessage;
        }

        this.userMessageInput =
          originalInput !== 'No input message found' ? originalInput : null;

        this.trace = nodes;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching agent trace:', err);
        this.errorMessage = 'Failed to load trace.';
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  generateDebugDocumentation(): void {
    if (!this.message?.id) return;

    this.isDebugModalVisible = true;
    this.isLoadingDebugDoc = true;
    this.debugDocumentationContent = null;

    this.agentTraceService.getDebugAnalysisUrl(this.message.id).subscribe({
      next: (data) => {
        if (data.url) {
          this.http.get(data.url, { responseType: 'text' }).subscribe({
            next: (content) => {
              this.debugDocumentationContent = content;
              this.isLoadingDebugDoc = false;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('Error fetching debug documentation content:', err);
              this.isLoadingDebugDoc = false;
              this.cdr.detectChanges();
              this.notificationService.createNotification(
                'error',
                'Error',
                'Failed to load debug documentation content.',
              );
            },
          });
        }
      },
      error: (err) => {
        console.error('Error generating debug documentation:', err);
        this.isLoadingDebugDoc = false;
        this.cdr.detectChanges();
        this.notificationService.createNotification(
          'error',
          'Error',
          'Failed to generate debug documentation.',
        );
      },
    });
  }

  closeDebugModal(): void {
    this.isDebugModalVisible = false;
  }

  copyDebugDoc(): void {
    if (this.debugDocumentationContent) {
      navigator.clipboard.writeText(this.debugDocumentationContent).then(() => {
        this.notificationService.createNotification(
          'success',
          'Success',
          'Copied to clipboard',
        );
      });
    }
  }

  downloadDebugDoc(): void {
    if (this.debugDocumentationContent) {
      const blob = new Blob([this.debugDocumentationContent], {
        type: 'text/markdown',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `debug_analysis_${this.message?.id}.md`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  }

  toggleMaximize(): void {
    this.isMaximized = !this.isMaximized;
  }

  setExpandAll(expand: boolean): void {
    this.isAllExpanded = expand;
    this.trace.forEach((node) => {
      node.isExpanded = expand;
      node.isStateExpanded = expand;
      node.isEventsExpanded = expand;
      if (node.tasks) {
        node.tasks.forEach((task) => (task.isExpanded = expand));
      }
    });
  }

  close() {
    this.isOpen = false;
    this.isOpenChange.emit(this.isOpen);
  }

  isObject(val: any): boolean {
    return val !== null && typeof val === 'object';
  }

  getTaskNames(node: TraceNode): string {
    if (!node.tasks || node.tasks.length === 0) return '';
    return node.tasks.map((t) => t.name).join('; ');
  }

  loadTaskAiLog(task: TraceTask): void {
    if (!task.aiLogId || task.isAiLogLoading) return;

    task.isAiLogLoading = true;
    this.cdr.detectChanges();

    this.aiLogControllerService
      .findById({ id: task.aiLogId } as any)
      .subscribe({
        next: (log: any) => {
          task.aiLogData = log;
          if (log.logFilePath) {
            this.fileManagementControllerService
              .getPrivateDownloadUrlByPath({ path: log.logFilePath })
              .subscribe({
                next: (res: any) => {
                  if (res.downloadUrl) {
                    this.http
                      .get(res.downloadUrl, { responseType: 'text' })
                      .subscribe({
                        next: (fileContent) => {
                          try {
                            const parsed = JSON.parse(fileContent);
                            task.aiLogData.curl = parsed.curl;
                            task.aiLogData.response = parsed.response;

                            if (typeof task.aiLogData.curl === 'string') {
                              const curlStr = task.aiLogData.curl;
                              const dIndex = curlStr.indexOf("-d '{");
                              if (dIndex !== -1) {
                                const endMatch = curlStr.lastIndexOf("}'");
                                if (endMatch > dIndex) {
                                  let jsonStr = curlStr.substring(
                                    dIndex + 4,
                                    endMatch + 1,
                                  );
                                  jsonStr = jsonStr
                                    .replace(/'\\''/g, "'")
                                    .replace(/\\'/g, "'");
                                  try {
                                    task.aiLogData.curl = JSON.parse(jsonStr);
                                  } catch (e) {
                                    task.aiLogData.curl = jsonStr;
                                  }
                                }
                              }
                            }

                            if (this.isObject(task.aiLogData.curl)) {
                              const inputObj = task.aiLogData.curl;
                              if (
                                inputObj.system_instruction?.parts?.[0]?.text
                              ) {
                                task.aiLogData.systemPromptText =
                                  inputObj.system_instruction.parts[0].text;
                                inputObj.system_instruction.parts[0].text =
                                  '[extracted to system prompt viewer]';
                              } else if (
                                inputObj.messages &&
                                Array.isArray(inputObj.messages)
                              ) {
                                const sysMsgIndex = inputObj.messages.findIndex(
                                  (m: any) =>
                                    m.role === 'system' ||
                                    m.role === 'developer',
                                );
                                if (
                                  sysMsgIndex !== -1 &&
                                  typeof inputObj.messages[sysMsgIndex]
                                    .content === 'string'
                                ) {
                                  task.aiLogData.systemPromptText =
                                    inputObj.messages[sysMsgIndex].content;
                                  inputObj.messages[sysMsgIndex].content =
                                    '[extracted to system prompt viewer]';
                                }
                              }
                            }
                            if (typeof task.aiLogData.response === 'string') {
                              const trimmed = task.aiLogData.response.trim();
                              if (
                                trimmed.startsWith('{') ||
                                trimmed.startsWith('[')
                              ) {
                                try {
                                  task.aiLogData.response = JSON.parse(trimmed);
                                } catch (e) {}
                              }
                            }
                          } catch (e) {
                            console.error('Failed to parse AI log file', e);
                          }
                          task.aiLogData.isExpanded = true;
                          task.isAiLogLoading = false;
                          this.cdr.detectChanges();
                        },
                        error: (err) => {
                          console.error(
                            'Failed to download AI Log file content:',
                            err,
                          );
                          task.aiLogData.isExpanded = true;
                          task.isAiLogLoading = false;
                          this.cdr.detectChanges();
                        },
                      });
                  } else {
                    task.aiLogData.isExpanded = true;
                    task.isAiLogLoading = false;
                    this.cdr.detectChanges();
                  }
                },
                error: (err) => {
                  console.error('Failed to get download URL for AI log:', err);
                  task.aiLogData.isExpanded = true;
                  task.isAiLogLoading = false;
                  this.cdr.detectChanges();
                },
              });
          } else {
            task.aiLogData.isExpanded = true;
            task.isAiLogLoading = false;
            this.cdr.detectChanges();
          }
        },
        error: (err) => {
          console.error('Failed to load AI Log:', err);
          task.isAiLogLoading = false;
          this.cdr.detectChanges();
          this.notificationService.createNotification(
            'error',
            'Error',
            'Failed to load AI Log details.',
          );
        },
      });
  }
}
